import { useEffect, useState, useCallback } from "react";
import { getPets, getPetsByOwnerId } from "../../api/petService";

export const usePetOwnerPets = (id, trigger, initialPage = 1, initialLimit = 10) => {
    const [pets, setPets] = useState({
        data: [],
        pagination: { page: initialPage, limit: initialLimit, totalPages: 1, total: 0 },
        sort: null,
        search: null,
    });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const fetchPets = useCallback(
        async (page = initialPage, limit = initialLimit, append = false, search = searchTerm) => {
        setLoading(true);
        try {
            const response = await getPetsByOwnerId(id, page, limit, search);
            setPets(prev => {
            if (append) {
                return {
                ...response,
                data: [...(prev.data || []), ...response.data],
                };
            }
            // Reset pagination when not appending (fresh search or initial load)
            return {
                ...response,
                pagination: { ...response.pagination, page }, 
            };
            });
            return response;
        } catch (err) {
            console.error("Failed to fetch pets:", err);
        } finally {
            setLoading(false);
        }
        },
        [initialPage, initialLimit, searchTerm]
    );

    useEffect(() => {
        fetchPets(initialPage, initialLimit, false, searchTerm);
    }, [trigger, fetchPets, initialPage, initialLimit, searchTerm]);

    const loadMore = async () => {
        const nextPage = pets.pagination.page + 1;
        if (nextPage <= pets.pagination.totalPages) {
        await fetchPets(nextPage, pets.pagination.limit, true, searchTerm);
        }
    };

    // 🔎 Called by SearchBox
    const searchFunction = async (page, keywords) => {
        setSearchTerm(keywords);
        // Reset results and pagination when searching
        await fetchPets(page, pets.pagination.limit, false, keywords);
    };

    const resetParent = async () => {
        setSearchTerm("");
        await fetchPets(initialPage, initialLimit, false, "");
    };

    return { pets, loading, loadMore, searchFunction, resetParent };
};

export const usePets = (trigger, initialPage = 1, initialLimit = 10) => {
    const [pets, setPets] = useState({
        data: [],
        pagination: { page: initialPage, limit: initialLimit, totalPages: 1, total: 0 },
        sort: null,
        search: null,
    });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    const fetchPets = useCallback(
        async (page = initialPage, limit = initialLimit, append = false, search = searchTerm) => {
        setLoading(true);
        try {
            const response = await getPets(page, limit, search);
            setPets(prev => {
            if (append) {
                return {
                ...response,
                data: [...(prev.data || []), ...response.data],
                };
            }
            // 🔑 Reset pagination when not appending (fresh search or initial load)
            return {
                ...response,
                pagination: { ...response.pagination, page }, 
            };
            });
            return response;
        } catch (err) {
            console.error("Failed to fetch pets:", err);
        } finally {
            setLoading(false);
        }
        },
        [initialPage, initialLimit, searchTerm]
    );

    useEffect(() => {
        if (trigger) {
        fetchPets(initialPage, initialLimit, false, searchTerm);
        }
    }, [trigger, fetchPets, initialPage, initialLimit, searchTerm]);

    const loadMore = async () => {
        const nextPage = pets.pagination.page + 1;
        if (nextPage <= pets.pagination.totalPages) {
        await fetchPets(nextPage, pets.pagination.limit, true, searchTerm);
        }
    };

    // 🔎 Called by SearchBox
    const searchFunction = async (page, keywords) => {
        setSearchTerm(keywords);
        // Reset results and pagination when searching
        await fetchPets(page, pets.pagination.limit, false, keywords);
    };

    const resetParent = async () => {
        setSearchTerm("");
        await fetchPets(initialPage, initialLimit, false, "");
    };

    return { pets, loading, loadMore, searchFunction, resetParent };
};